import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { usd, fmtTime } from "@/lib/format";
import { StatCard, Pill } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { Bot, Users, CalendarClock, FileSignature, HardHat, DollarSign, AlertTriangle, Send, TrendingUp, Percent } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [brief, setBrief] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [appts, setAppts] = useState([]);
  const [q, setQ] = useState("");
  const [ans, setAns] = useState(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    api.get("/dashboard/briefing").then((r) => setBrief(r.data)).catch(() => {});
    api.get("/cashflow/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
    api.get("/appointments").then((r) => setAppts(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const ask = async () => {
    if (!q.trim()) return;
    setAsking(true); setAns(null);
    try { const { data } = await api.post("/ai/chat", { question: q }); setAns(data.answer); }
    catch { toast.error("AI unavailable"); } finally { setAsking(false); }
  };

  const m = brief?.metrics || {};
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-navy">{(() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })()}, {(user?.name || "").split(" ")[0]}</h1>
        </div>
      </div>

      {/* Daily AI Briefing hero */}
      <div data-testid="daily-briefing" className="rounded-2xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden hero-grain"
        style={{ background: "linear-gradient(120deg,#0B2545 0%,#07192E 55%,#0D305C 100%)" }}>
        <div className="absolute -right-16 -top-16 w-64 h-64 bg-brandred/20 rounded-full blur-3xl" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-brandred flex items-center justify-center"><Bot className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /></div>
            <span className="font-display font-bold uppercase tracking-wider text-sm">Daily AI Business Briefing · 7:00 AM</span>
          </div>
          {brief?.narrative && <p className="text-slate-200 text-base leading-relaxed mb-4 max-w-3xl">{brief.narrative}</p>}
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 font-num text-sm sm:text-base">
            {(brief?.lines || ["Loading briefing…"]).map((l, i) => (
              <div key={i} className="flex items-start gap-2 border-l-2 border-brandred/60 pl-3">{l}</div>
            ))}
          </div>
        </div>
      </div>

      {/* Cash flow alerts */}
      {alerts.map((a, i) => (
        <div key={i} data-testid="cashflow-alert" onClick={() => nav("/cashflow")}
          className={`rounded-xl p-4 flex items-start gap-3 cursor-pointer border-2 ${a.level === "danger" ? "bg-dangerr/5 border-dangerr/30" : "bg-amberw/5 border-amberw/30"}`}>
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${a.level === "danger" ? "text-dangerr" : "text-amberw"}`} />
          <div>
            <div className="font-bold text-navy">{a.title}</div>
            <div className="text-sm text-slate-600">{a.message}</div>
            {a.drivers?.length ? <div className="text-xs text-slate-500 mt-1">Drivers: {a.drivers.join(" · ")}</div> : null}
          </div>
        </div>
      ))}

      {/* Stat grid — every number clickable */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard testid="stat-new-leads" label="New Leads" value={m.new_leads ?? "—"} icon={Users} onClick={() => nav("/crm")} />
        <StatCard testid="stat-appointments" label="Appointments" value={m.appointments ?? "—"} icon={CalendarClock} onClick={() => nav("/crm")} />
        <StatCard testid="stat-proposals-out" label="Proposals Out" value={usd(m.proposals_outstanding_cents)} icon={FileSignature} tone="navy" onClick={() => nav("/proposals")} />
        <StatCard testid="stat-signed" label="Signed (Mo)" value={usd(m.contracts_signed_cents)} icon={DollarSign} tone="green" onClick={() => nav("/proposals")} />
        <StatCard testid="stat-starting-today" label="Roofs Today" value={m.roofs_starting_today ?? "—"} icon={HardHat} onClick={() => nav("/production")} />
        <StatCard testid="stat-expected" label="Payments Expected" value={usd(m.payments_expected_cents)} icon={DollarSign} tone="green" onClick={() => nav("/quickbooks")} />
        <StatCard testid="stat-overdue" label="Overdue A/R" value={m.overdue_receivables ?? "—"} icon={AlertTriangle} tone="danger" onClick={() => nav("/quickbooks")} />
        <StatCard testid="stat-below-margin" label="Below Margin" value={m.jobs_below_margin ?? "—"} icon={Percent} tone="amber" onClick={() => nav("/quickbooks")} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Ask AI */}
        <div className="lg:col-span-2 mb-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Bot className="w-5 h-5 text-navy" />
            <h3 className="font-display font-bold uppercase text-lg text-navy">Ask your Business Manager</h3>
          </div>
          <div className="flex gap-2">
            <input data-testid="dash-ai-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="How much did we make this month?"
              className="flex-1 border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy" />
            <button data-testid="dash-ai-send" onClick={ask} disabled={asking}
              className="bg-navy text-white px-4 rounded-xl border-2 border-navy flex items-center gap-2 font-semibold text-sm disabled:opacity-60">
              <Send className="w-4 h-4" /> {asking ? "…" : "Ask"}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {["How much money did we make this month?", "Which sales rep has the highest closing percentage?", "Which jobs are below 30% gross margin?", "Who owes us money?"].map((s) => (
              <button key={s} onClick={() => setQ(s)} className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-full">{s}</button>
            ))}
          </div>
          {ans && <div data-testid="dash-ai-answer" className="mt-4 bg-slate-50 rounded-xl p-4 text-sm text-slate-700 whitespace-pre-wrap border border-slate-200">{ans}</div>}
        </div>

        {/* Upcoming appointments */}
        <div className="mb-card p-5">
          <h3 className="font-display font-bold uppercase text-lg text-navy mb-3">Upcoming Visits</h3>
          <div className="space-y-2">
            {appts.length === 0 && <p className="text-sm text-slate-400">No appointments</p>}
            {appts.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                <div>
                  <div className="text-sm font-semibold text-navy">{a.contact?.name || "—"}</div>
                  <div className="text-xs text-slate-500">{fmtTime(a.scheduled_at)}</div>
                </div>
                <Pill tone={a.type === "inspection" ? "blue" : "red"}>{a.type?.replace("_", " ")}</Pill>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reps closing */}
      {m.reps?.length ? (
        <div className="mb-card p-5">
          <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5 text-navy" /><h3 className="font-display font-bold uppercase text-lg text-navy">Rep Closing Rates</h3></div>
          <div className="grid sm:grid-cols-2 gap-3">
            {m.reps.map((r) => (
              <div key={r.name} className="flex items-center gap-3">
                <div className="w-full">
                  <div className="flex justify-between text-sm"><span className="font-semibold text-navy">{r.name}</span><span className="font-num text-navy">{r.closing_pct}%</span></div>
                  <div className="h-2 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-brandred rounded-full" style={{ width: `${r.closing_pct}%` }} /></div>
                  <div className="text-xs text-slate-400 mt-0.5">{r.closed}/{r.total_leads} leads</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
